/**
 * Pure style-document transform that turns the OpenFreeMap "positron"
 * basemap into a weather-data backdrop: everything that competes with the
 * forecast overlay is hidden, the political borders are restyled with a
 * bolder color/width, and a coastline stroke is injected above the overlay
 * band.
 *
 * This is the platform-neutral twin of the webapp's runtime
 * `applyForecastBasemapStyle` (which mutates a live MapLibre GL JS map via
 * setPaintProperty et al.): mobile fetches the style JSON, passes it through
 * here and hands the transformed document to MapLibre Native. Constants are
 * shared so the two render identically.
 *
 * Positron only reveals régions/départements at zoom 8 — we pull them down
 * to the national view and give them weight.
 *
 * Mobile anchoring: the raster overlay is placed by NUMERIC index
 * (`overlayLayerIndex`, below the boundary band — same stacking as the
 * webapp) and the vector features by id (`symbolAnchorLayerId`, below the
 * place labels). The split exists because MapLibre Native (Android)
 * cannot `beforeId` a layer that came with the base style JSON — see the
 * doc on the return type — while inserting at an index is synchronous
 * and needs no lookup at all.
 */

// OpenFreeMap "positron" style — a muted, keyless vector basemap built as a
// data backdrop (shaded relief + water, minimal everything else).
export const FORECAST_BASEMAP_STYLE_URL =
  'https://tiles.openfreemap.org/styles/positron';

// Coarse cross-model guardrail: the overlay is a fixed raster at the model's
// native grid density, so zooming far past it only magnifies grid cells.
// Cap zoom at a still-useful level rather than an all-blur close-up.
export const FORECAST_OVERLAY_MAX_ZOOM = 11;

// Basemap layers whose whole `source-layer` is noise on a weather map: roads,
// route-number shields + road names (the clutter that appeared on zoom),
// rivers, and airport markers. Hidden wholesale by source-layer so this
// survives the basemap renaming individual layers.
export const CLUTTER_SOURCE_LAYERS: ReadonlySet<string> = new Set([
  'transportation', // road/rail/pier geometry
  'transportation_name', // road names + route-number shields
  'waterway', // rivers, canals, streams
  'aerodrome_label', // airport markers
]);

// The two boundary line layers positron ships (OpenMapTiles schema).
export const COUNTRY_LAYER = 'boundary_2'; // admin_level 2
export const REGION_DEPT_LAYER = 'boundary_3'; // admin_level 3–6 (régions + départements)

// Muted cool slates (Positron-style): a quiet reference that reads over both
// warm (temperature) and cool (precip) overlays without competing with the
// data. Country is darkest, régions mid, départements lightest — but all
// dark enough to hold up over a saturated summer-temperature field.
export const COUNTRY_BORDER_COLOR = 'hsl(213,18%,30%)';
export const REGION_BORDER_COLOR = 'hsl(213,18%,32%)';
export const DEPT_BORDER_COLOR = 'hsl(213,14%,42%)';

// The injected coastline stroke: positron draws the sea as a `water` FILL
// layer low in the stack, so the forecast overlay — draped just below the
// boundary band — paints over the coastline. Re-drawing it as a stroke of
// the basemap's own water source keeps land/sea readable over the data.
export const COASTLINE_LAYER_ID = 'forecast-coastline';

// admin_level ≤ 4 ⇒ région (or state); ≥ 5 ⇒ département (or county). Fresh
// array per call so the same node is never shared across expressions.
const isRegion = () => ['<=', ['get', 'admin_level'], 4];

type StyleLayer = {
  id: string;
  type?: string;
  source?: string;
  'source-layer'?: string;
  minzoom?: number;
  maxzoom?: number;
  filter?: unknown;
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
  [key: string]: unknown;
};

export type ForecastBasemapStyle = {
  layers?: StyleLayer[];
  [key: string]: unknown;
};

export type TransformedForecastBasemapStyle = {
  style: ForecastBasemapStyle;
  // Anchor for the vector features (isolines / barbs / fronts): id of the
  // first symbol layer, so they are inserted just below the place labels —
  // above the boundary band, like the webapp.
  //
  // The RASTER overlay cannot use the same mechanism against the boundary
  // layer (as the webapp does) because of a MapLibre Native (Android) gap:
  // `beforeId` resolves the target via `waitForLayer`, which checks the
  // style synchronously and otherwise waits for `layerAdded()` — a callback
  // that only ever fires for layers added through the React bridge, never
  // for a layer that was already part of the base style JSON. If the target
  // isn't synchronously present at that exact check (observed to reliably
  // not be, for the boundary layer of this style), the wait never resolves
  // and the raster layer is silently never added. Hence `overlayLayerIndex`
  // below. The first symbol layer as a `beforeId` target resolves reliably
  // (empirically, on device) so the vector anchor keeps the id form, which
  // survives the index shifts caused by the overlay insertions.
  symbolAnchorLayerId: string | undefined;
  // Anchor for the raster overlay: pass to Layer's `layerIndex` to sit below
  // the political borders + coastline + labels but above the (hidden) roads
  // and the water fill — the webapp's exact stacking. Index insertion is
  // synchronous native-side (`addLayerAt`), sidestepping the `waitForLayer`
  // gap above. One BELOW the first boundary layer (native inserts above the
  // layer at the given index); falls back to below the injected coastline
  // for a boundary-less style.
  overlayLayerIndex: number | undefined;
};

const hideLayer = (layer: StyleLayer): StyleLayer => ({
  ...layer,
  layout: {...layer.layout, visibility: 'none'},
});

// Country border: darkest of the set but still quiet — a thin slate line,
// always on.
const boostCountryBorder = (layer: StyleLayer): StyleLayer => ({
  ...layer,
  paint: {
    ...layer.paint,
    'line-color': COUNTRY_BORDER_COLOR,
    'line-opacity': 0.9,
    'line-width': ['interpolate', ['linear'], ['zoom'], 3, 1, 7, 1.8],
  },
});

// Régions vs départements share one layer — branch on admin_level. Régions:
// a light reference from z4 (positron hides the layer until zoom 8 — reveal
// it at the national view instead). Départements: thinner still, fade in
// z6.5 → z8 so the national view isn't a mesh of internal borders.
const boostRegionDeptBorder = (layer: StyleLayer): StyleLayer => ({
  ...layer,
  minzoom: 4,
  maxzoom: 24,
  paint: {
    ...layer.paint,
    'line-color': ['case', isRegion(), REGION_BORDER_COLOR, DEPT_BORDER_COLOR],
    // Positron ships boundary_3 dotted at [1,1] — at phone densities over a
    // busy color field that reads as faint speckle. Near-solid dashes put
    // real ink on the line while keeping the dashed = internal-border
    // language; the country/région distinction is carried by width.
    // (line-dasharray cannot be data-driven, so both levels share it.)
    'line-dasharray': [3, 1.5],
    'line-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      4,
      ['case', isRegion(), 1, 0.5],
      8,
      ['case', isRegion(), 1.5, 1],
    ],
    // ['zoom'] is only legal as the input of a TOP-LEVEL step/interpolate
    // (style-spec rule). GL JS merely drops an offending property, but
    // MapLibre Native (iOS/Android) rejects it and the whole layer never
    // renders — so the zoom ramp is the root and the per-level branch
    // lives in the outputs. Near-opaque so the muted slates hold up over
    // a saturated (e.g. summer-temperature) overlay; the level staging
    // comes from color/width, plus the départements' z6.5 → z8 fade-in.
    'line-opacity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      6.5,
      ['case', isRegion(), 0.9, 0],
      8,
      ['case', isRegion(), 0.9, 0.9],
    ],
  },
});

const transformLayer = (layer: StyleLayer): StyleLayer => {
  const sourceLayer = layer['source-layer'];
  if (sourceLayer !== undefined && CLUTTER_SOURCE_LAYERS.has(sourceLayer)) {
    return hideLayer(layer);
  }
  if (layer.id === COUNTRY_LAYER) {
    return boostCountryBorder(layer);
  }
  if (layer.id === REGION_DEPT_LAYER) {
    return boostRegionDeptBorder(layer);
  }
  return layer;
};

const coastlineLayer = (waterSource: string): StyleLayer => ({
  id: COASTLINE_LAYER_ID,
  type: 'line',
  source: waterSource,
  'source-layer': 'water',
  // Only the sea: the `water` layer also carries lakes and wide rivers (the
  // Loire etc.) as polygons — outlining those drew rivers over the map. Keep
  // just `ocean` so this is a true coastline.
  filter: ['==', ['get', 'class'], 'ocean'],
  paint: {
    // Matches the boosted country border so coast and borders read as one set.
    'line-color': COUNTRY_BORDER_COLOR,
    'line-width': ['interpolate', ['linear'], ['zoom'], 3, 1, 7, 1.8],
  },
});

export const transformForecastBasemapStyle = (
  style: ForecastBasemapStyle,
): TransformedForecastBasemapStyle => {
  const inputLayers = style.layers;
  if (inputLayers === undefined) {
    return {
      style: {...style},
      symbolAnchorLayerId: undefined,
      overlayLayerIndex: undefined,
    };
  }

  const layers = inputLayers.map(transformLayer);

  // Anchor is computed on the pre-insertion list; inserting the coastline
  // below the first symbol layer does not change its id.
  const firstSymbolIndex = layers.findIndex(
    (layer) => layer.type === 'symbol',
  );
  const symbolAnchorLayerId =
    firstSymbolIndex === -1 ? undefined : layers[firstSymbolIndex].id;

  // Idempotency: a document that already went through the transform keeps
  // its single coastline stroke.
  if (!layers.some((layer) => layer.id === COASTLINE_LAYER_ID)) {
    // The coastline strokes the basemap's own water polygons — reuse the
    // source of the (already loaded) water fill layer.
    const waterSource =
      layers.find(
        (layer) => layer['source-layer'] === 'water' && layer.type === 'fill',
      )?.source ?? 'openmaptiles';
    const coast = coastlineLayer(waterSource);
    if (firstSymbolIndex === -1) {
      layers.push(coast);
    } else {
      layers.splice(firstSymbolIndex, 0, coast);
    }
  }

  // Computed on the final array so overlay insertions land exactly below
  // the boundary band. Positron carries every admin line in the `boundary`
  // source-layer; its first entry marks the bottom of that band. Native's
  // addLayerAt inserts ABOVE the layer currently at the index (Android JNI
  // resolves `before = layers[index + 1]`; iOS matches) — hence anchor - 1,
  // and being one-low is visually free anyway: everything between the water
  // fill and the boundary band is hidden clutter.
  const firstBoundaryIndex = layers.findIndex(
    (layer) => layer['source-layer'] === 'boundary',
  );
  const overlayAnchorIndex =
    firstBoundaryIndex === -1
      ? layers.findIndex((layer) => layer.id === COASTLINE_LAYER_ID)
      : firstBoundaryIndex;
  const overlayLayerIndex = Math.max(overlayAnchorIndex - 1, 0);

  return {style: {...style, layers}, symbolAnchorLayerId, overlayLayerIndex};
};
